import { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { cities } from "../data/cities"; // <-- adjust path if needed

// 🔥 pick only 8 featured cities
const featuredCities = cities.filter((c) =>
  [
    "paris",
    "rome",
    "barcelona",
    "london",
    "amsterdam",
    "vienna",
    "prague",
    "florence",
  ].includes(c.slug)
);

const CitySlider = ({ onCityClick = () => { } }) => {
  const [index, setIndex] = useState(0);

  // 🔹 touch & drag state
  const [startX, setStartX] = useState(null);
  const [currentX, setCurrentX] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [cardWidth, setCardWidth] = useState(380);
  const draggedRef = useRef(false);

  const total = featuredCities.length;

  // Responsive card spacing to maintain stable layout
  useEffect(() => {
    const updateWidth = () => {
      setCardWidth(window.innerWidth < 768 ? 300 : 400);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const prev = useCallback(() => {
    setIndex((prev) => (prev - 1 + total) % total);
  }, [total]);

  const next = useCallback(() => {
    setIndex((prev) => (prev + 1) % total);
  }, [total]);

  /* ================= TOUCH HANDLERS ================= */
  const handleDragStart = (e) => {
    const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    setStartX(clientX);
    setCurrentX(clientX);
    setIsDragging(true);
    draggedRef.current = false;
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    setCurrentX(clientX);
    if (Math.abs(clientX - startX) > 10) {
      draggedRef.current = true;
    }
  };

  const handleDragEnd = () => {
    if (!isDragging || startX === null || currentX === null) {
      setIsDragging(false);
      setStartX(null);
      setCurrentX(null);
      return;
    }

    const diff = startX - currentX;
    const threshold = 50;

    if (diff > threshold) next();
    else if (diff < -threshold) prev();

    setIsDragging(false);
    setStartX(null);
    setCurrentX(null);
    
    setTimeout(() => { draggedRef.current = false; }, 50);
  };

  const handleLinkClick = (e) => {
    if (draggedRef.current) e.preventDefault();
  };

  const dragOffset = isDragging && currentX !== null && startX !== null ? currentX - startX : 0;

  return (
    <section className="w-full py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        {/* ===== HEADING ===== */}
        <div className="mb-10 md:mb-16 text-center">
          <h2 className="text-3xl md:text-5xl font-serif text-[#1f2d1f]">
            Discover Iconic Cities
          </h2>
          <p className="mt-4 text-[#6b7280] text-lg max-w-2xl mx-auto">
            Handpicked destinations known for culture, beauty, and unforgettable
            experiences.
          </p>
        </div>

        {/* ===== SLIDER ===== */}
        <div
          className="relative h-[480px] md:h-[540px] flex items-center justify-center touch-pan-y select-none"
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          {featuredCities.map((city, i) => {
            let offset = i - index;

            // Handle infinite wrap-around
            if (offset > total / 2) offset -= total;
            if (offset < -total / 2) offset += total;

            // Render wider range to prevent visual popping and allow smooth entering
            if (Math.abs(offset) > 2) return null;

            const isCenter = offset === 0;
            const absOffset = Math.abs(offset);

            // Damped drag for premium physics feel
            const dampedDrag = dragOffset * 0.85;
            const baseTranslate = offset * cardWidth;
            const translateX = baseTranslate + dampedDrag;

            // Stable dimensions, pure opacity for depth, subtle scale
            const scale = isCenter ? 1 : absOffset === 1 ? 0.98 : 0.94;
            const opacity = isCenter ? 1 : absOffset === 1 ? 0.6 : 0;
            const zIndex = 20 - absOffset;

            // CSS Transitions - disable during drag for instantaneous tracking
            const transitionStyle = isDragging
              ? "none"
              : "transform 0.8s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1)";

            return (
              <div
                key={city.slug}
                className="absolute flex justify-center items-center cursor-grab active:cursor-grabbing"
                style={{
                  transform: `translate3d(${translateX}px, 0, 0) scale(${scale})`,
                  opacity,
                  zIndex,
                  transition: transitionStyle,
                  willChange: "transform, opacity",
                  backfaceVisibility: "hidden",
                  pointerEvents: isCenter ? "auto" : "none",
                }}
              >
                <div className="relative w-[280px] sm:w-[380px] h-[440px] md:h-[500px] rounded-[24px] md:rounded-[32px] overflow-hidden group bg-[#111] shadow-2xl">
                  {/* IMAGE (Linked) */}
                  <Link to={`/cities/${city.slug}`} onClick={handleLinkClick} className="absolute inset-0 z-0 select-none">
                    <img
                      src={city.image}
                      alt={`Things to do in ${city.name}, ${city.country}`}
                      loading="lazy"
                      draggable="false"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
                  </Link>

                  {/* CONTENT */}
                  <div className="absolute bottom-8 left-8 right-8 text-white z-10 pointer-events-none">
                    <h3 className="text-2xl md:text-3xl font-semibold leading-tight drop-shadow-md">{city.name}</h3>
                    <p className="text-xs md:text-sm text-white/80 mt-1 uppercase tracking-widest font-medium drop-shadow-md">{city.country}</p>
                    <p className="text-sm text-white/90 mt-4 line-clamp-2 leading-relaxed drop-shadow-sm">
                      {city.desc}
                    </p>

                    {/* SEO Topic Cluster Links */}
                    <div className="mt-5 flex flex-col gap-2 pointer-events-auto">
                      <Link to={`/cities/${city.slug}`} onClick={handleLinkClick} className="text-sm font-semibold text-[#dce775] hover:text-white transition-colors drop-shadow-md">
                        Explore {city.name} →
                      </Link>
                      <Link to={`/itineraries/${city.slug}`} onClick={handleLinkClick} className="text-xs font-medium text-white/80 hover:text-white transition-colors drop-shadow-md">
                        {city.name} itinerary
                      </Link>
                      <Link to={`/blogs/${city.slug}-travel-guide`} onClick={handleLinkClick} className="text-xs font-medium text-white/80 hover:text-white transition-colors drop-shadow-md">
                        {city.name} travel guide
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* ===== ARROWS ===== */}
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-2 md:left-8 z-30 h-10 w-10 md:h-12 md:w-12 rounded-full bg-white/90 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.12)] flex items-center justify-center hover:bg-white hover:scale-105 text-[#1f2d1f] transition-all group"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-70 group-hover:opacity-100 group-hover:-translate-x-0.5 transition-transform"><polyline points="15 18 9 12 15 6"></polyline></svg>
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-2 md:right-8 z-30 h-10 w-10 md:h-12 md:w-12 rounded-full bg-white/90 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.12)] flex items-center justify-center hover:bg-white hover:scale-105 text-[#1f2d1f] transition-all group"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-transform"><polyline points="9 18 15 12 9 6"></polyline></svg>
          </button>
        </div>

        {/* ===== VIEW ALL LINK ===== */}
        <div className="mt-10 flex justify-center md:justify-end md:pr-12">
          <Link
            to="/cities"
            aria-label="Explore all cities"
            className="text-sm md:text-base font-semibold text-[#5b6f00] hover:text-[#4a5a00] hover:underline underline-offset-4 transition-all"
          >
            Explore all cities →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CitySlider;
